import React, { useState } from 'react';
import { linkForm } from './workflowDefinitionService';

interface Props {
  taskId: string;
  onClose: () => void;
  onLinked: () => void;
}

export const WorkflowFormLinker: React.FC<Props> = ({ taskId, onClose, onLinked }) => {
  const [formNumber, setFormNumber] = useState('');
  const [formName, setFormName] = useState('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [filingSequence, setFilingSequence] = useState<number | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLink = async () => {
    if (!formNumber.trim()) {
      setError('Form number is required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await linkForm(taskId, {
        form_number: formNumber.trim().toUpperCase(),
        form_name: formName.trim() || undefined,
        is_mandatory: isMandatory,
        filing_sequence: filingSequence,
      });
      onLinked();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Link Court Form</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {error && (
          <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-2 text-sm mb-3">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Form Number *</label>
            <input
              type="text"
              value={formNumber}
              onChange={(e) => setFormNumber(e.target.value)}
              placeholder="e.g. FL-100"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Form Name</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Petition - Marriage/Domestic Partnership"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isMandatory}
                onChange={(e) => setIsMandatory(e.target.checked)}
                className="h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded focus:ring-lex-accent"
              />
              Mandatory
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 dark:text-gray-300">Filing Sequence:</label>
              <input
                type="number"
                min={1}
                value={filingSequence || ''}
                onChange={(e) => setFilingSequence(e.target.value ? Number(e.target.value) : undefined)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            onClick={handleLink}
            disabled={saving || !formNumber.trim()}
            className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover disabled:bg-lex-accent-hover rounded-lg shadow-sm transition-colors"
          >
            {saving ? 'Linking...' : 'Link Form'}
          </button>
        </div>
      </div>
    </div>
  );
};
